import GoBoard from '@sabaki/go-board';
import type { GameTree, NodeObject, Vertex } from '@/shared/ui/GOViewer/model/types';
import { parseCoordinates } from '@/shared/ui/GOViewer/model/utils';
import type { CurrentMove } from './types';

const POSITION_PROPERTIES = ['SZ', 'H', 'AE', 'AB', 'AW', 'PL', 'B', 'W'] as const;
const DEFAULT_CHECKPOINT_INTERVAL = 25;
const IDLE_BUDGET_MS = 5;

export type BoardPosition = {
    board: GoBoard;
    currentMove: CurrentMove;
    moveNumber: number;
};

type CacheEntry = BoardPosition & {
    tree: GameTree;
    nodeSignature: string;
    parentVersion: number;
    version: number;
};

type WarmTask = {
    tree: GameTree;
    stack: Array<{ node: NodeObject; depth: number }>;
    visitedIds: Set<number>;
};

export type PositionCacheStats = {
    positions: number;
    calculated: number;
    reused: number;
};

export type IdleDeadlineLike = {
    timeRemaining: () => number;
};

export type PositionCacheScheduler = {
    schedule: (callback: (deadline: IdleDeadlineLike) => void) => unknown;
    cancel: (handle: unknown) => void;
};

export type BoardPositionCacheOptions = {
    checkpointInterval?: number;
    scheduler?: PositionCacheScheduler | null;
};

function createBrowserScheduler(): PositionCacheScheduler | null {
    if (typeof window === 'undefined') return null;

    if (typeof window.requestIdleCallback === 'function') {
        return {
            schedule: (callback) => window.requestIdleCallback(callback),
            cancel: (handle) => window.cancelIdleCallback(handle as number),
        };
    }

    return {
        schedule: (callback) =>
            window.setTimeout(() => {
                const startedAt = performance.now();
                callback({
                    timeRemaining: () => Math.max(0, IDLE_BUDGET_MS - (performance.now() - startedAt)),
                });
            }, 16),
        cancel: (handle) => window.clearTimeout(handle as number),
    };
}

function getNodeSignature(node: NodeObject): string {
    return POSITION_PROPERTIES.map((property) => `${property}:${node.data[property]?.join('\u001f') ?? ''}`).join(
        '\u001e'
    );
}

function applySetupProperties(board: GoBoard, node: NodeObject) {
    node.data.AE?.forEach((coord) => board.set(parseCoordinates(coord), 0));
    node.data.AB?.forEach((coord) => board.set(parseCoordinates(coord), 1));
    node.data.AW?.forEach((coord) => board.set(parseCoordinates(coord), -1));
}

function getMove(node: NodeObject): { sign: 1 | -1; vertex: Vertex } | null {
    const rawCoord = node.data.B?.[0] ?? node.data.W?.[0];
    if (rawCoord === undefined) return null;

    return {
        sign: node.data.B ? 1 : -1,
        vertex: rawCoord === '' ? [-1, -1] : parseCoordinates(rawCoord),
    };
}

/**
 * Stores immutable board snapshots for resolved nodes and warms the rest of a
 * game tree in small idle chunks. One instance should represent one logical
 * board mode (server game, analysis, SGF viewer, etc.).
 */
export class BoardPositionCache {
    private entries = new Map<number, CacheEntry>();
    private version = 0;
    private calculated = 0;
    private reused = 0;
    private readonly checkpointInterval: number;
    private readonly scheduler: PositionCacheScheduler | null;
    private warmTask: WarmTask | null = null;
    private warmHandle: unknown = null;
    private warmedTree: GameTree | null = null;

    constructor({ checkpointInterval = DEFAULT_CHECKPOINT_INTERVAL, scheduler }: BoardPositionCacheOptions = {}) {
        this.checkpointInterval = Math.max(1, checkpointInterval);
        this.scheduler = scheduler === undefined ? createBrowserScheduler() : scheduler;
    }

    resolve(tree: GameTree, nodeId: number): BoardPosition | null {
        const targetNode = tree.get(nodeId);
        const boardSize = Number(tree.root.data.SZ?.[0]);
        if (!targetNode || !boardSize) return null;

        const directEntry = this.entries.get(nodeId);
        if (directEntry?.tree === tree) {
            this.reused++;
            return directEntry;
        }

        const path: NodeObject[] = [];
        let parentEntry: CacheEntry | null = null;
        let node: NodeObject | null = targetNode;
        while (node) {
            const cachedAncestor = this.entries.get(node.id);
            if (cachedAncestor?.tree === tree) {
                parentEntry = cachedAncestor;
                this.reused++;
                break;
            }
            path.push(node);
            node = node.parentId === null ? null : tree.get(node.parentId);
        }
        path.reverse();

        for (const pathNode of path) {
            const nodeSignature = getNodeSignature(pathNode);
            const parentVersion = parentEntry?.version ?? 0;
            const existing = this.entries.get(pathNode.id);

            if (existing && existing.nodeSignature === nodeSignature && existing.parentVersion === parentVersion) {
                existing.tree = tree;
                parentEntry = existing;
                this.reused++;
                continue;
            }

            parentEntry = this.calculateEntry(tree, pathNode, boardSize, nodeSignature, parentEntry);
            this.entries.set(pathNode.id, parentEntry);
        }

        return parentEntry;
    }

    scheduleWarm(tree: GameTree) {
        if (!this.scheduler || this.warmedTree === tree || this.warmTask?.tree === tree) return;

        this.cancelWarm();
        this.warmTask = {
            tree,
            stack: [{ node: tree.root, depth: 0 }],
            visitedIds: new Set<number>(),
        };
        this.scheduleNextWarmChunk();
    }

    seedFrom(source: BoardPositionCache) {
        this.clear();
        this.entries = new Map([...source.entries].map(([nodeId, entry]) => [nodeId, { ...entry }] as const));
        this.version = source.version;
    }

    clear() {
        this.cancelWarm();
        this.entries.clear();
        this.version = 0;
        this.calculated = 0;
        this.reused = 0;
        this.warmedTree = null;
    }

    getStats(): PositionCacheStats {
        return {
            positions: this.entries.size,
            calculated: this.calculated,
            reused: this.reused,
        };
    }

    private calculateEntry(
        tree: GameTree,
        node: NodeObject,
        boardSize: number,
        nodeSignature: string,
        parentEntry: CacheEntry | null
    ): CacheEntry {
        let board: GoBoard;
        const hasSetupProperties = !!(node.data.AE?.length || node.data.AB?.length || node.data.AW?.length);

        if (parentEntry) {
            const move = getMove(node);

            if (hasSetupProperties) {
                board = parentEntry.board.clone();
                applySetupProperties(board, node);
                // Stone setup starts a new ko history, but does not change prisoners.
                board = new GoBoard(board.signMap)
                    .setCaptures(1, parentEntry.board.getCaptures(1))
                    .setCaptures(-1, parentEntry.board.getCaptures(-1));
                if (move && move.vertex[0] >= 0) board = board.makeMove(move.sign, move.vertex);
            } else if (move && move.vertex[0] >= 0) {
                board = parentEntry.board.makeMove(move.sign, move.vertex);
            } else {
                board = parentEntry.board;
            }
        } else {
            board = GoBoard.fromDimensions(boardSize);
            const handicap = Number(tree.root.data.H?.[0]);
            if (handicap > 1 && !node.data.AB?.length) {
                board.getHandicapPlacement(handicap).forEach((stone) => board.set(stone, 1));
            }
            applySetupProperties(board, node);
            const move = getMove(node);
            if (move && move.vertex[0] >= 0) board = board.makeMove(move.sign, move.vertex);
        }

        const move = getMove(node);
        const moveNumber = (parentEntry?.moveNumber ?? 0) + (move ? 1 : 0);
        const handicap = Number(tree.root.data.H?.[0]);
        let currentMove: CurrentMove = move
            ? {
                  vertex: move.vertex,
                  sign: move.sign === 1 ? -1 : 1,
                  number: moveNumber,
              }
            : (parentEntry?.currentMove ?? {
                  vertex: [-1, -1],
                  sign: handicap > 1 ? -1 : 1,
                  number: 0,
              });

        if (parentEntry && hasSetupProperties) currentMove = { ...currentMove, vertex: null };
        if (node.data.PL) currentMove = { ...currentMove, sign: node.data.PL[0] === 'W' ? -1 : 1 };

        this.calculated++;
        return {
            tree,
            board,
            currentMove,
            moveNumber,
            nodeSignature,
            parentVersion: parentEntry?.version ?? 0,
            version: ++this.version,
        };
    }

    private scheduleNextWarmChunk() {
        if (!this.scheduler || !this.warmTask || this.warmHandle !== null) return;
        this.warmHandle = this.scheduler.schedule((deadline) => {
            this.warmHandle = null;
            this.runWarmChunk(deadline);
        });
    }

    private runWarmChunk(deadline: IdleDeadlineLike) {
        const task = this.warmTask;
        if (!task) return;

        let processedAtLeastOne = false;
        while (task.stack.length > 0 && (!processedAtLeastOne || deadline.timeRemaining() > 1)) {
            const item = task.stack.pop()!;
            const { node, depth } = item;
            processedAtLeastOne = true;
            task.visitedIds.add(node.id);

            for (let index = node.children.length - 1; index >= 0; index--) {
                task.stack.push({ node: node.children[index], depth: depth + 1 });
            }

            const isCheckpoint = depth % this.checkpointInterval === 0;
            const isBranchPoint = node.children.length > 1;
            const isLeaf = node.children.length === 0;
            if (isCheckpoint || isBranchPoint || isLeaf) this.resolve(task.tree, node.id);
        }

        if (task.stack.length > 0) {
            this.scheduleNextWarmChunk();
            return;
        }

        for (const nodeId of this.entries.keys()) {
            if (!task.visitedIds.has(nodeId)) this.entries.delete(nodeId);
        }
        this.warmedTree = task.tree;
        this.warmTask = null;
    }

    private cancelWarm() {
        if (this.scheduler && this.warmHandle !== null) this.scheduler.cancel(this.warmHandle);
        this.warmHandle = null;
        this.warmTask = null;
    }
}

export const defaultBoardPositionCache = new BoardPositionCache();
